import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Form, Label, Input, FormFeedback } from "@/Components/ui";
import { useAuthContext } from "../../Components/AuthContext";
import { userService } from "../../api/services/user.service";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import * as Yup from "yup";
import { useFormik } from "formik";
import toast from "react-hot-toast";

const Profile = () => {
  const { userInfo } = useAuthContext();
  const [profileData, setProfileData] = useState<any>(null);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await userService.getUserInfo();
      setProfileData(response.data);
      validation.setValues({
        username: response.data.username || "",
        business_name: response.data.business_name || "",
        email: response.data.email || "",
      });
    } catch (error) {
      console.error("Failed to fetch user profile", error);
      toast.error("프로필 정보를 불러오는데 실패했습니다.");
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: profileData?.username || "",
      business_name: profileData?.business_name || "",
      email: profileData?.email || "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("이름을 입력해주세요"),
      business_name: Yup.string().nullable(),
      email: Yup.string().email("유효한 이메일을 입력해주세요").nullable(),
    }),
    onSubmit: async (values) => {
      if (!profileData) return;
      try {
        await userService.updateUserInfo(profileData.id, {
          username: values.username,
          business_name: values.business_name || null,
          email: values.email || null,
        });
        toast.success("프로필이 성공적으로 업데이트 되었습니다.");
        fetchUserProfile();
      } catch (error) {
        console.error(error);
        toast.error("프로필 업데이트에 실패했습니다.");
      }
    },
  });

  const passwordValidation = useFormik({
    initialValues: {
      password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(6, "비밀번호는 최소 6자 이상이어야 합니다.")
        .required("새 비밀번호를 입력해주세요"),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("password"), null as any], "비밀번호가 일치하지 않습니다.")
        .required("비밀번호 확인을 입력해주세요"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        await userService.updateUserPassword(values.password);
        toast.success("비밀번호가 성공적으로 변경되었습니다.");
        resetForm();
      } catch (error) {
        console.error(error);
        toast.error("비밀번호 변경에 실패했습니다.");
      }
    },
  });

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="내 프로필" pageTitle="사용자" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">프로필 정보 수정</h4>
                </CardHeader>
                <CardBody>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <Row>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="username" className="form-label">
                            이름
                          </Label>
                          <Input
                            name="username"
                            id="username"
                            className="form-control"
                            placeholder="Enter your name"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.username}
                            invalid={
                              validation.touched.username && validation.errors.username ? true : false
                            }
                          />
                          {validation.touched.username && validation.errors.username ? (
                            <FormFeedback type="invalid">{validation.errors.username as string}</FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="email" className="form-label">
                            이메일
                          </Label>
                          <Input
                            name="email"
                            id="email"
                            className="form-control"
                            placeholder="Enter your email"
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email}
                            invalid={
                              validation.touched.email && validation.errors.email ? true : false
                            }
                          />
                          {validation.touched.email && validation.errors.email ? (
                            <FormFeedback type="invalid">{validation.errors.email as string}</FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="business_name" className="form-label">
                            제휴사명 (Business Name)
                          </Label>
                          <Input
                            name="business_name"
                            id="business_name"
                            className="form-control"
                            placeholder="Enter your business name (optional)"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.business_name}
                            invalid={
                              validation.touched.business_name && validation.errors.business_name ? true : false
                            }
                          />
                          {validation.touched.business_name && validation.errors.business_name ? (
                            <FormFeedback type="invalid">{validation.errors.business_name as string}</FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="phone" className="form-label">
                            전화번호 (수정 불가)
                          </Label>
                          <Input
                            name="phone"
                            id="phone"
                            className="form-control"
                            type="text"
                            value={profileData?.phone_num || ""}
                            disabled
                          />
                        </div>
                      </Col>
                    </Row>
                    <div className="text-end mt-4">
                      <Button type="submit" color="primary">
                        저장하기
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className="my-4">
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">비밀번호 변경</h4>
                </CardHeader>
                <CardBody>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      passwordValidation.handleSubmit();
                      return false;
                    }}
                  >
                    <Row>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="password" className="form-label">
                            새 비밀번호
                          </Label>
                          <Input
                            name="password"
                            id="password"
                            className="form-control"
                            placeholder="Enter new password"
                            type="password"
                            onChange={passwordValidation.handleChange}
                            onBlur={passwordValidation.handleBlur}
                            value={passwordValidation.values.password}
                            invalid={
                              passwordValidation.touched.password && passwordValidation.errors.password ? true : false
                            }
                          />
                          {passwordValidation.touched.password && passwordValidation.errors.password ? (
                            <FormFeedback type="invalid">{passwordValidation.errors.password as string}</FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="confirm_password" className="form-label">
                            비밀번호 확인
                          </Label>
                          <Input
                            name="confirm_password"
                            id="confirm_password"
                            className="form-control"
                            placeholder="Confirm new password"
                            type="password"
                            onChange={passwordValidation.handleChange}
                            onBlur={passwordValidation.handleBlur}
                            value={passwordValidation.values.confirm_password}
                            invalid={
                              passwordValidation.touched.confirm_password && passwordValidation.errors.confirm_password ? true : false
                            }
                          />
                          {passwordValidation.touched.confirm_password && passwordValidation.errors.confirm_password ? (
                            <FormFeedback type="invalid">{passwordValidation.errors.confirm_password as string}</FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                    </Row>
                    <div className="text-end mt-4">
                      <Button type="submit" color="primary">
                        비밀번호 변경
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Profile;
